import { Box } from "@bca/ui-library";
import React, { useState } from "react";
import styled, { css } from "styled-components";

import { DESKTOP_PAGE_SIZE, VrmQueryState } from "../../pages";
import {
  NUMBER_OF_DAYS_OPTIONS,
  PurchasesUrlQueryParams,
} from "../APICalls/buildGetPurchasesUrl";
import { AppliedCheckboxResult } from "../Hooks/useAppliedCheckBoxes";
import Filter from "../Interfaces/Filter";
import DateRange from "./DateRange";
import FilterCategories from "./FilterCategories";
import FiltersDesktopLoader from "./FiltersDesktopLoader";
import FiltersHeader from "./FiltersHeader";
import VrmSearchInput from "./VrmSearchInput";

const Container = styled(Box)`
  ${css({
    border: "normalBorder",
    color: "grey-blue",
  })}
`;

interface FiltersProps {
  filters: Filter[];
  isLoading?: boolean;
  desktopVrmQuery: {
    q: string;
    triggeredBy: string;
  };
  setDesktopVrmQuery: (vrmQuery: VrmQueryState) => void;
  onClearAll: () => void;
  isClearAllDisabled?: boolean;
  updateAdditionalFilters?;
  purchasesUrlQueryParams?: PurchasesUrlQueryParams;
  setPurchasesUrlQueryParams: React.Dispatch<
    React.SetStateAction<PurchasesUrlQueryParams>
  >;
  isToUseAuthZero?: boolean;
}

const FiltersDesktop: React.FunctionComponent<
  FiltersProps & AppliedCheckboxResult
> = ({
  filters,
  isLoading,
  setDesktopVrmQuery,
  desktopVrmQuery,
  onClearAll,
  isClearAllDisabled = true,
  updateAdditionalFilters,
  purchasesUrlQueryParams,
  setPurchasesUrlQueryParams,
  appliedCheckboxes,
  setAppliedCheckboxesCheckBoxGroup,
  setAppliedCheckboxesCheckBoxGroupRemoveChip,
  setAppliedCheckboxesSingleSelect,
  isToUseAuthZero
}) => {
  const [isFilterCategoryLoading, setIsFilterCategoryLoading] = useState(false);

  const onEnter = () => {
    if (!shouldShowVrmQueryWarning) {
      setDesktopVrmQuery({
        q: desktopVrmQuery.q,
        triggeredBy: "ENTER",
      });
    }
  };

  const vrmQueryOnChange = (q) => {
    setDesktopVrmQuery({
      q,
      triggeredBy: "KEYPRESS",
    });
  };

  const setSelectedFilter = (numberofDays: NUMBER_OF_DAYS_OPTIONS) => {
    setPurchasesUrlQueryParams({
      ...purchasesUrlQueryParams,
      numberofDays,
      page: 1,
      pageSize: DESKTOP_PAGE_SIZE,
    });
    window.scrollTo(0, 0);
  };

  const shouldShowVrmQueryWarning =
    desktopVrmQuery.q.length < 3 && desktopVrmQuery.q.length !== 0;
  const shouldShowDesktopFilters =
    isLoading === false && isFilterCategoryLoading === false;

  return (
    <Container
      as="aside"
      width="100%"
      maxWidth={234}
      mr={5}
      flexDirection="column"
      display="flex"
    >
      {!shouldShowDesktopFilters && <FiltersDesktopLoader />}
      {shouldShowDesktopFilters && (
        <>
          <VrmSearchInput
            vrmQuery={desktopVrmQuery.q}
            onChange={vrmQueryOnChange}
            warning={shouldShowVrmQueryWarning}
            onEnter={onEnter}
          />
          <FiltersHeader
            onClearAll={onClearAll}
            isClearAllDisabled={isClearAllDisabled}
          />
          <DateRange
            selectedFilter={purchasesUrlQueryParams.numberofDays}
            setSelectedFilter={setSelectedFilter}
            filters={filters}
          />
        </>
      )}
      <FilterCategories
        appliedCheckboxes={appliedCheckboxes}
        setAppliedCheckboxesCheckBoxGroup={setAppliedCheckboxesCheckBoxGroup}
        setAppliedCheckboxesCheckBoxGroupRemoveChip={
          setAppliedCheckboxesCheckBoxGroupRemoveChip
        }
        purchasesUrlQueryParams={purchasesUrlQueryParams}
        updateAdditionalFilters={updateAdditionalFilters}
        setIsFilterCategoryLoading={setIsFilterCategoryLoading}
        isLoading={isLoading}
        setAppliedCheckboxesSingleSelect={setAppliedCheckboxesSingleSelect}
        isToUseAuthZero={isToUseAuthZero}
      />
    </Container>
  );
};

export default FiltersDesktop;
